import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { Page } from "../../components/Page";
import { DashboardContent } from "./components/DashboardContent";
import { OnboardContent } from "./components/OnboardContent";
import { HotelDetailModal } from "./components/HotelDetailModal";
import { JobTriggersPanel } from "./components/JobTriggersPanel";
import { DiagnosticsContent } from "./components/DiagnosticsContent";
import Button from "../../components/Button";
import { Tooltip } from "../../components/tooltip";
import { useDiagnostics } from "./hooks";
import { formatDateTime } from "./utils";
import { DiagnosticsSnapshot } from "./types";

type MetasearchTab = "dashboard" | "operations" | "diagnostics";

const TAB_CONFIG: { key: MetasearchTab; label: string }[] = [
  { key: "dashboard", label: "Dashboard" },
  { key: "operations", label: "Operations" },
  { key: "diagnostics", label: "Diagnostics" },
];

function parseTab(value: string | null): MetasearchTab {
  const validKeys = TAB_CONFIG.map((t) => t.key);
  return validKeys.includes(value as MetasearchTab)
    ? (value as MetasearchTab)
    : "dashboard";
}

export function Metasearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTab(searchParams.get("tab"));
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [showOnboard, setShowOnboard] = useState(false);
  const { data: diagnosticsData } = useDiagnostics();
  const diagnosticsSnapshots: DiagnosticsSnapshot[] = Array.isArray(diagnosticsData) ? diagnosticsData : [];
  const diagnosticsUpdatedAt = diagnosticsSnapshots[0]?.updatedAt;

  const setActiveTab = useCallback(
    (tab: MetasearchTab) => {
      setSearchParams({ tab }, { replace: true });
    },
    [setSearchParams]
  );

  const handleViewConfig = (configId: string) => {
    setSelectedConfigId(configId);
  };

  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1">
        <h1 className="text-2xl font-semibold text-black mb-4">Metasearch</h1>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm border border-[#E9E9EB] w-fit">
            {TAB_CONFIG.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  "px-4 py-2 text-sm font-medium rounded-md transition-colors",
                  activeTab === tab.key
                    ? "bg-primary text-white"
                    : "text-gray-600 hover:bg-gray-100"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {activeTab === "diagnostics" && diagnosticsUpdatedAt && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400">
                Last updated {formatDateTime(diagnosticsUpdatedAt)}
              </span>
              <Tooltip id="diagnostics-staleness" className="!w-[240px]">
                <p className="text-xs">
                  Diagnostics refresh each time the cron job runs and may not
                  reflect the latest onboarded hotels.
                </p>
              </Tooltip>
            </div>
          )}
          {activeTab === "dashboard" && !showOnboard && (
            <Button className="ml-auto" onClick={() => setShowOnboard(true)}>
              + New Hotel
            </Button>
          )}
        </div>

        {activeTab === "dashboard" && !showOnboard && (
          <DashboardContent onViewConfig={handleViewConfig} />
        )}
        {activeTab === "dashboard" && showOnboard && (
          <OnboardContent
            onSuccess={() => setShowOnboard(false)}
            onCancel={() => setShowOnboard(false)}
          />
        )}
        {activeTab === "operations" && <JobTriggersPanel />}
        {activeTab === "diagnostics" && <DiagnosticsContent />}
      </div>

      {selectedConfigId && (
        <HotelDetailModal
          isOpen={!!selectedConfigId}
          configId={selectedConfigId}
          onClose={() => setSelectedConfigId(null)}
        />
      )}
    </Page>
  );
}
